import { DEFAULT_TIMEZONE } from '@yoyoclub/shared';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import { dateLocale } from '../i18n/locale';
import { localize } from '../i18n/script';
import { passApi, type UserPassDetail } from '../services/passApi';
import { trackPurchase } from '../utils/analytics';

export default function PassPurchaseSuccess() {
  const { t, i18n } = useTranslation('pass');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, getToken } = useAuth();

  const userPassId = searchParams.get('userPassId');
  const stripeSessionId = searchParams.get('session_id');

  const [userPass, setUserPass] = useState<UserPassDetail | null>(null);
  const [loading, setLoading] = useState(!!userPassId || !!stripeSessionId);
  const trackedRef = useRef(false);

  // Path A: credit-only purchase — userPassId is in the URL
  useEffect(() => {
    const fetchPassDetails = async () => {
      if (!userPassId) return;

      try {
        setLoading(true);
        const token = await getToken();
        if (!token) return;
        const data = await passApi.getUserPassById(userPassId, token);
        setUserPass(data);
        // GA4: track pass purchase conversion (fire once only)
        if (!trackedRef.current) {
          trackedRef.current = true;
          const title = localize(data.pass.titleCN, data.pass.titleEN, i18n.language);
          trackPurchase({
            type: 'pass',
            transactionId: userPassId,
            value: data.paymentAmount ? data.paymentAmount / 100 : undefined,
            title,
          });
        }
      } catch {
        // Non-critical — still show generic success
      } finally {
        setLoading(false);
      }
    };

    fetchPassDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language intentionally excluded to prevent re-firing on language switch
  }, [userPassId, getToken]);

  // Path B: Stripe purchase — only session_id in the URL (no userPassId)
  useEffect(() => {
    const fetchFromStripe = async () => {
      if (userPassId || !stripeSessionId) return;

      try {
        setLoading(true);
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
        const response = await fetch(`${apiUrl}/payments/checkout-session/${stripeSessionId}`);
        if (!response.ok) return;
        const result = await response.json();
        if (result.success && result.data && !trackedRef.current) {
          trackedRef.current = true;
          trackPurchase({
            type: 'pass',
            transactionId: result.data.bookingId || stripeSessionId,
            value: result.data.totalAmount ? result.data.totalAmount / 100 : undefined,
            title: result.data.activityTitle,
          });
        }
      } catch {
        // Non-critical
      } finally {
        setLoading(false);
      }
    };

    fetchFromStripe();
  }, [stripeSessionId, userPassId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">{t('purchaseSuccess.loadingDetails')}</p>
        </div>
      </div>
    );
  }

  const passTitle = userPass
    ? localize(userPass.pass.titleCN, userPass.pass.titleEN, i18n.language)
    : null;

  const expiresFormatted = userPass
    ? new Date(userPass.expiresAt).toLocaleDateString(dateLocale(i18n.language), {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: DEFAULT_TIMEZONE,
      })
    : null;

  const totalPaid = userPass ? (userPass.paymentAmount / 100).toFixed(2) : null;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        {/* Success Icon */}
        <div className="mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <svg
              className="w-8 h-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>

        {/* Success Message */}
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('purchaseSuccess.title')}</h1>
        <p className="text-gray-600 mb-6">{t('purchaseSuccess.subtitle')}</p>

        <div className="space-y-4 text-gray-600 mb-8">
          {/* Pass Details */}
          {(userPass || stripeSessionId) && (
            <div className="bg-gray-50 rounded-lg p-4 text-left space-y-2">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                {t('purchaseSuccess.passDetails')}
              </h3>
              {passTitle && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t('purchaseSuccess.passName')}</span>
                  <span className="font-medium text-gray-900 text-right max-w-[60%]">
                    {passTitle}
                  </span>
                </div>
              )}
              {userPass && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t('purchaseSuccess.sessionsAvailable')}</span>
                  <span className="font-medium text-gray-900">{userPass.remainingSessions}</span>
                </div>
              )}
              {expiresFormatted && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t('purchaseSuccess.validUntil')}</span>
                  <span className="font-medium text-gray-900">{expiresFormatted}</span>
                </div>
              )}
              {totalPaid && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t('purchaseSuccess.totalPaid')}</span>
                  <span className="font-medium text-green-600">${totalPaid}</span>
                </div>
              )}
            </div>
          )}

          {/* Email confirmation */}
          <p className="text-sm">{t('purchaseSuccess.confirmationSent')}</p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {isAuthenticated && (
            <button
              onClick={() => navigate('/dashboard?tab=passes')}
              className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-primary-dark transition-colors"
            >
              {t('purchaseSuccess.viewMyPasses')}
            </button>
          )}
          <button
            onClick={() => navigate('/activities')}
            className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:border-gray-400 transition-colors"
          >
            {t('purchaseSuccess.browseActivities')}
          </button>
        </div>
      </div>
    </div>
  );
}
